import * as React from 'react';
import { Box, Skeleton, SxProps, Theme } from '@mui/material';

export interface HeaderListSkeletonProps {
    /**
     * Nombre d'éléments dans le fil d'Ariane
     * @default 3
     */
    breadcrumbItemCount?: number;

    /**
     * Largeur de chaque élément du breadcrumb
     * @default 120
     */
    breadcrumbItemWidth?: number | string;

    /**
     * Nombre de boutons d'action à afficher
     * @default 1
     */
    actionButtonCount?: number;

    /**
     * Largeur des boutons d'action
     * @default 180
     */
    actionButtonWidth?: number | string;

    /**
     * Hauteur des boutons d'action
     * @default 48
     */
    actionButtonHeight?: number | string;

    /**
     * Afficher le titre sous le breadcrumb
     * @default true
     */
    withTitle?: boolean;

    /**
     * Largeur du titre
     * @default 250
     */
    titleWidth?: number | string;

    /**
     * Styles additionnels à appliquer au conteneur principal
     */
    sx?: SxProps<Theme>;
}

/**
 * Squelette de chargement pour le composant ListHeader.
 *
 * Reproduit visuellement la structure d'un en-tête de liste :
 * - Fil d'Ariane à gauche (avec titre optionnel)
 * - Bouton(s) d'action à droite
 *
 * @example
 * ```tsx
 * <HeaderListSkeleton
 *     breadcrumbItemCount={3}
 *     actionButtonCount={1}
 *     withTitle
 * />
 * ```
 */
export const HeaderListSkeleton: React.FunctionComponent<HeaderListSkeletonProps> = function ({
    breadcrumbItemCount = 3,
    breadcrumbItemWidth = 120,
    actionButtonCount = 1,
    actionButtonWidth = 180,
    actionButtonHeight = 48,
    withTitle = true,
    titleWidth = 250,
    sx,
}) {
    const breadcrumbItems = React.useMemo(
        () => Array.from({ length: breadcrumbItemCount }, (_, index) => index),
        [breadcrumbItemCount],
    );

    const actionButtons = React.useMemo(
        () => Array.from({ length: actionButtonCount }, (_, index) => index),
        [actionButtonCount],
    );

    return (
        <Box
            data-testid="header-list-skeleton"
            sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                height: '95%',
                width: '95%',
                margin: '15px',
                ...sx,
            }}
        >
            {/* Zone gauche : Breadcrumb + Titre */}
            <Box
                data-testid="header-list-skeleton-left"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                }}
            >
                {/* Breadcrumb */}
                <Box
                    data-testid="header-list-skeleton-breadcrumb"
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    {breadcrumbItems.map((index) => (
                        <React.Fragment key={index}>
                            <Skeleton
                                variant="text"
                                width={breadcrumbItemWidth}
                                height={24}
                                animation="wave"
                            />
                            {index < breadcrumbItemCount - 1 && (
                                <Skeleton
                                    variant="text"
                                    width={16}
                                    height={24}
                                    animation="wave"
                                />
                            )}
                        </React.Fragment>
                    ))}
                </Box>

                {/* Titre */}
                {withTitle && (
                    <Skeleton
                        data-testid="header-list-skeleton-title"
                        variant="text"
                        width={titleWidth}
                        height={36}
                        animation="wave"
                    />
                )}
            </Box>

            {/* Zone droite : Boutons d'action */}
            <Box
                data-testid="header-list-skeleton-actions"
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 1,
                }}
            >
                {actionButtons.map((index) => (
                    <Skeleton
                        key={index}
                        variant="rounded"
                        width={actionButtonWidth}
                        height={actionButtonHeight}
                        animation="wave"
                        sx={{ borderRadius: '12px' }}
                    />
                ))}
            </Box>
        </Box>
    );
};
